import { useState, useRef } from 'react';
import { useAuth }           from '@/auth/AuthContext';
import { CortexMark }        from '@/components/ui/CortexLogo';
import { ShieldOff, ArrowRight, Eye, EyeOff } from 'lucide-react';

export function LoginPage() {
  const { signIn, status, error } = useAuth();

  const [code,     setCode]     = useState('');
  const [showCode, setShowCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLoading = status === 'authenticating';
  const isDenied  = status === 'denied';
  const canSubmit = code.trim().length > 0 && !isLoading;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    void signIn(code.trim());
  }

  return (
    <div className="min-h-[100dvh] bg-ink-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-full bg-brand-50 flex items-center justify-center mb-4">
            <CortexMark className="w-12 h-12 text-brand-600" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">
            VIREON
          </h1>
          <p className="text-xs text-ink-400 mt-1 tracking-widest uppercase">
            Enter your access password
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-ink-100 p-7">

          {/* Error banner */}
          {isDenied && error && (
            <div className="mb-5 flex items-start gap-3 p-3.5 rounded-xl bg-signal-danger/8 border border-signal-danger/20">
              <ShieldOff className="w-4 h-4 text-signal-danger shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-signal-danger leading-snug">
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            <div>
              <label
                htmlFor="access-code"
                className="block text-xs font-semibold text-ink-700 uppercase tracking-wider mb-2"
              >
                Password
              </label>

              <div className="relative">
                <input
                  ref={inputRef}
                  id="access-code"
                  type={showCode ? 'text' : 'password'}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder=""
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  disabled={isLoading}
                  className="w-full rounded-xl border border-ink-200 bg-white px-4 py-3 pr-11 text-sm font-mono text-ink-900 placeholder-ink-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50 transition"
                />
                <button
                  type="button"
                  onClick={() => { setShowCode(v => !v); inputRef.current?.focus(); }}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 transition"
                  aria-label={showCode ? 'Hide code' : 'Show code'}
                >
                  {showCode
                    ? <EyeOff className="w-4 h-4" />
                    : <Eye    className="w-4 h-4" />}
                </button>
              </div>

              <p className="mt-2 text-[11px] text-ink-400 leading-snug">
                Use one of the access codes provided for your site.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white hover:bg-brand-700 active:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span>Connecting…</span>
                </>
              ) : (
                <>
                  <span>Open dashboard</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Status hint shown during long waits */}

          </form>

        </div>

        {/* Footer */}
        <p className="text-center text-[11px] text-ink-400 mt-5 leading-relaxed">
          Enter the access password for your site.<br />
          Contact your administrator if you do not have one.
        </p>

      </div>
    </div>
  );
}
